/*
Andreas Arvidsson
https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html
*/

import type { RefObject, TargetedKeyboardEvent, VNode, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { classNames } from "@gss/common/classNames";
import { InputCheckbox } from "@gss/components/InputCheckbox";
import { useClickOutside } from "@gss/hooks/useClickOutside";
import "./MultiSelect.css";
import { useConst } from "@gss/hooks/useConst";

let nextId = 1;

export type MultiSelectChild = VNode<{
    value: string;
    disabled?: boolean;
    children: string;
}>;

interface Props {
    values: string[];
    onChange: (value: string, checked: boolean) => void;
    children: MultiSelectChild[];
    label?: string;
    placeholder?: string;
    size?: "lg" | "sm";
    className?: string;
    disabled?: boolean;
    "aria-labelledby"?: string;
    "aria-label"?: string;
}

export function MultiSelect({
    values,
    onChange,
    children,
    label: labelIn,
    placeholder,
    size,
    className,
    disabled,
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel,
}: Props): JSX.Element {
    const [open, setOpen] = useState(false);
    const id = useConst(() => `multi-select-${nextId++}`);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    // Close list when clickig outside
    useClickOutside(listRef, () => setOpen(false), {
        disabled: !open,
    });

    const label = labelIn ?? getLabel(children, values, placeholder);

    const onKeyDown = (e: TargetedKeyboardEvent<HTMLButtonElement>) => {
        switch (e.key) {
            case "Enter":
            case "ArrowUp":
            case "ArrowDown":
            case " ":
                setOpen(true);
                e.preventDefault();
                break;
            // no default
        }
    };

    return (
        <div
            className={classNames(
                "multi-select",
                size != null && `multi-select-${size}`,
                className,
            )}
        >
            <button
                id={id}
                type="button"
                className={classNames(
                    "form-select",
                    size != null && `form-select-${size}`,
                )}
                ref={buttonRef}
                disabled={disabled}
                onClick={() => setOpen(!open)}
                onKeyDown={onKeyDown}
                title={label}
                aria-haspopup="listbox"
                aria-expanded={open ? "true" : undefined}
                aria-label={
                    ariaLabel != null ? `${ariaLabel} ${label}` : undefined
                }
                aria-labelledby={
                    ariaLabelledby != null
                        ? `${ariaLabelledby}  ${id}`
                        : undefined
                }
            >
                {label}
            </button>
            {open && (
                <MultiSelectList
                    id={id}
                    listRef={listRef}
                    values={values}
                    ariaLabel={ariaLabel}
                    ariaLabelledby={ariaLabelledby}
                    onChange={onChange}
                    close={() => {
                        setOpen(false);
                        buttonRef.current?.focus();
                    }}
                >
                    {children}
                </MultiSelectList>
            )}
        </div>
    );
}

function getLabel(
    children: MultiSelectChild[],
    values: string[],
    placeholder?: string,
): string | undefined {
    if (values.length > 0) {
        return children
            .filter((c) => values.includes(c.props.value))
            .map((c) => c.props.children)
            .join(", ");
    }
    if (placeholder != null) {
        return placeholder;
    }
    return undefined;
}

/* MultiSelectList */

interface MultiSelectListProps {
    id: string;
    listRef: RefObject<HTMLUListElement | null>;
    values: string[];
    onChange: (value: string, checked: boolean) => void;
    close: () => void;
    ariaLabel?: string;
    ariaLabelledby?: string;
    children: MultiSelectChild[];
}

function MultiSelectList({
    id,
    listRef,
    children,
    values,
    onChange,
    close,
    ariaLabel,
    ariaLabelledby,
}: MultiSelectListProps) {
    const [activeValue, setActiveValue] = useState(children[0].props.value);
    const [search, setSearch] = useState({ value: "", time: 0 });
    const activeId = `${id}-active`;

    // Focus on list during mount/load
    useEffect(() => {
        listRef.current?.focus();
    }, []);

    // Search for element with search text in name
    useEffect(() => {
        const e = children.find((c) => {
            const label = String(c.props.children).toLowerCase();
            return label.startsWith(search.value);
        });
        if (e) {
            setActiveValue(e.props.value);
        }
    }, [search]);

    const moveActive = (diff: number) => {
        const i =
            children.findIndex((c) => c.props.value === activeValue) + diff;
        if (i > -1 && i < children.length) {
            setActiveValue(children[i].props.value);
        }
    };

    const onKeyDown = (e: TargetedKeyboardEvent<HTMLUListElement>) => {
        switch (e.key) {
            case "ArrowUp":
                moveActive(-1);
                break;
            case "ArrowDown":
                moveActive(1);
                break;
            case "Escape":
            case "Enter":
            case "Tab":
                close();
                break;
            case "Home":
                setActiveValue(children[0].props.value);
                break;
            case "End":
                setActiveValue(children[children.length - 1].props.value);
                break;
            case " ":
                onChange(activeValue, !values.includes(activeValue));
                break;
            default:
                if (e.key.length === 1) {
                    const key = e.key.toLowerCase();
                    const time = Date.now();
                    setSearch({
                        time,
                        value:
                            time - search.time > 1000
                                ? key
                                : search.value + key,
                    });
                } else {
                    return;
                }
        }
        e.preventDefault();
    };

    const renderElement = (c: MultiSelectChild) => {
        const checked = values.includes(c.props.value);
        const isActive = activeValue === c.props.value;
        const disabled = Boolean(c.props.disabled);
        return (
            <li
                key={c.props.value}
                id={isActive ? activeId : undefined}
                role="option"
                aria-selected={checked}
                title={c.props.children}
                onClick={
                    disabled
                        ? undefined
                        : () => onChange(c.props.value, !checked)
                }
                onMouseOver={() => setActiveValue(c.props.value)}
            >
                <InputCheckbox
                    checked={checked}
                    disabled={disabled}
                    tabIndex={-1}
                    readOnly
                />
                {c.props.children}
            </li>
        );
    };

    return (
        <ul
            ref={listRef}
            tabIndex={-1}
            role="listbox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            aria-multiselectable
            aria-activedescendant={activeId}
            onKeyDown={onKeyDown}
        >
            {children.map(renderElement)}
        </ul>
    );
}
